// The patient's photo check: three shots, asked for at each tray change.
//
// Why a prompt rather than a free-form "add a photo" button: the doctor reviews a SERIES. Three
// fixed poses per tray can be laid side by side across the treatment; an irregular pile of selfies
// cannot. The app therefore asks at the moment the tray changes, and stops asking once the set for
// the current tray is complete.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  formatDate,
  PHOTO_POSES,
  PHOTO_POSE_HINTS,
  PHOTO_POSE_LABELS,
  type PatientTreatment,
  type PhotoPose,
  type ProgressPhoto,
} from '@alignova/shared';
import { patientApi, patientPhotoUrl, uploadProgressPhoto } from '../lib/patientApi.ts';
import { IconCamera, IconCheck } from '../ui/icons.tsx';

export function PatientPhotoCheck({ treatment }: { treatment: PatientTreatment }) {
  const [photos, setPhotos] = useState<ProgressPhoto[] | null>(null);
  const [busy, setBusy] = useState<PhotoPose | null>(null);
  const [pct, setPct] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const pending = useRef<PhotoPose | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    patientApi.photos().then(setPhotos).catch(() => setPhotos([]));
  }, []);
  useEffect(load, [load]);

  const tray = treatment.currentTray ?? null;
  // "Done" means done for THIS tray — last tray's front shot doesn't answer for this one.
  const doneForTray = new Set(
    (photos ?? []).filter((p) => p.trayNo === tray).map((p) => p.pose),
  );
  const remaining = PHOTO_POSES.filter((p) => !doneForTray.has(p));
  const complete = photos !== null && remaining.length === 0;

  // Ask when a change is due (or overdue), and keep asking until the set is complete.
  const changeDue = (treatment.daysToChange ?? 99) <= 0;
  const askNow = !complete && (changeDue || doneForTray.size > 0);

  function pick(pose: PhotoPose) {
    pending.current = pose;
    setErr(null);
    input.current?.click();
  }

  async function onFile(file: File | undefined) {
    const pose = pending.current;
    pending.current = null;
    if (!file || !pose) return;
    setBusy(pose);
    setPct(0);
    setErr(null);
    try {
      setPhotos(await uploadProgressPhoto(file, { pose }, setPct));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That photo could not be sent. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  if (photos === null) return null;

  const mine = photos.slice(0, 6);

  return (
    <section className="animate-rise rounded-xl border border-line bg-surface p-5">
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        className="hidden"
        onChange={(e) => {
          void onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Photo check</div>
          <h2 className="mt-0.5 text-lg font-bold tracking-tight text-ink">
            {complete
              ? `Tray ${tray ?? ''} photos sent`
              : askNow
                ? 'Time for your photos'
                : 'Send your progress photos'}
          </h2>
        </div>
        {complete && (
          <span className="mt-1 inline-flex shrink-0 items-center gap-1 rounded-full bg-status-progress-bg px-2.5 py-1 text-xs font-semibold text-status-progress-fg">
            <IconCheck size={14} />
            Done
          </span>
        )}
      </div>

      <p className="mt-1 max-w-[46ch] text-sm text-muted">
        {complete
          ? 'Your lab and your doctor can see these. Take the next set when you change trays.'
          : 'Three quick photos so your doctor can check your teeth are tracking. Aligners in for all three.'}
      </p>

      {err && (
        <p className="mt-3 rounded-md bg-status-urgent-bg px-3 py-2 text-sm text-status-urgent-fg">{err}</p>
      )}

      <ul className="mt-4 space-y-2">
        {PHOTO_POSES.map((pose) => {
          const done = doneForTray.has(pose);
          const uploading = busy === pose;
          return (
            <li key={pose}>
              <button
                type="button"
                onClick={() => pick(pose)}
                disabled={!!busy}
                className={`tap-row flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left disabled:opacity-60 ${
                  done ? 'border-status-progress-fg/30 bg-status-progress-bg/40' : 'border-line bg-surface'
                }`}
              >
                <span
                  className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${
                    done ? 'bg-status-progress-fg text-white' : 'bg-surface-2 text-muted'
                  }`}
                >
                  {done ? <IconCheck size={20} /> : <IconCamera size={20} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-ink">{PHOTO_POSE_LABELS[pose]}</span>
                  <span className="block text-xs text-muted">
                    {uploading ? `Sending… ${pct}%` : done ? 'Sent' : PHOTO_POSE_HINTS[pose]}
                  </span>
                </span>
                {!done && !uploading && (
                  <span className="shrink-0 text-sm font-semibold text-accent">{doneForTray.size ? 'Add' : 'Take'}</span>
                )}
              </button>
              {uploading && (
                <div className="mt-1 h-1 overflow-hidden rounded bg-surface-2">
                  <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {mine.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Your photos</div>
          <ul className="no-scrollbar snap-strip flex gap-2 overflow-x-auto">
            {mine.map((p) => (
              <li key={p.id} className="shrink-0">
                <figure className="w-24">
                  <img
                    src={patientPhotoUrl(p.id)}
                    alt={`${PHOTO_POSE_LABELS[p.pose]} taken ${formatDate(p.takenAt)}`}
                    loading="lazy"
                    className="h-24 w-24 rounded-md border border-line object-cover"
                  />
                  <figcaption className="mt-1 truncate text-[11px] text-muted">
                    {p.trayNo ? `Tray ${p.trayNo}` : formatDate(p.takenAt)}
                  </figcaption>
                </figure>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
